import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';
import { type Locale } from '@/i18n/config';
import { useLocale } from '@/i18n/LocaleProvider';
import { useWorkspace } from '@/hooks/useWorkspace';

interface Props {
  isOpen: boolean;
  pendingLocale: Locale | null;
  onClose: () => void;
  onConfirmed?: () => void;
}

// Confirms a tenant-wide language change for authenticated users. UI locale
// and workspace API update run only after the user confirms.
export function LanguageSwitchConfirmDialog({
  isOpen,
  pendingLocale,
  onClose,
  onConfirmed,
}: Props) {
  const { t, i18n } = useTranslation('common');
  const { setLocale } = useLocale();
  const { setLanguageMutation } = useWorkspace();

  if (!pendingLocale) return null;

  const languageName =
    pendingLocale === 'en' ? t('language.option_english') : t('language.option_french');

  const submit = async () => {
    try {
      await setLocale(pendingLocale);
      await setLanguageMutation.mutateAsync(pendingLocale);
      const targetLanguageName =
        pendingLocale === 'en'
          ? i18n.t('language.option_english', { lng: pendingLocale, ns: 'common' })
          : i18n.t('language.option_french', { lng: pendingLocale, ns: 'common' });
      toast.success(
        i18n.t('language.update_success', {
          lng: pendingLocale,
          ns: 'common',
          language: targetLanguageName,
        }),
      );
      onConfirmed?.();
      onClose();
    } catch {
      toast.error(t('language.update_error'));
      onClose();
    }
  };

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('language.confirm_title', { language: languageName })}
      message={t('language.confirm_body')}
      buttons={[
        {
          label: t('language.confirm_cancel'),
          onClick: onClose,
          type: 'outlined',
          intent: 'secondary',
          disabled: setLanguageMutation.isPending,
        },
        {
          label: t('language.confirm_submit', { language: languageName }),
          onClick: submit,
          intent: 'secondary',
          loading: setLanguageMutation.isPending,
          disabled: setLanguageMutation.isPending,
        },
      ]}
    />
  );
}
